import { useEffect, useRef } from 'react';
import { useAnalytics } from '@center-technologies/analytics';
import type { EmbeddedSagaProgress, SagaProgress } from 'types/sagaProgress';

/** Where a saga-progress view is mounted — the `surface` every saga-progress event carries. */
export type SagaProgressSurface = 'signup' | 'deploy_dialog' | 'preview_world';

const isRun = (progress: EmbeddedSagaProgress | null): progress is SagaProgress =>
  progress != null && progress.runId !== null;

/**
 * The saga-progress analytics the spec authorises (§ Analytics), fired from the molecule so every
 * surface reports them the same way:
 *
 * - `saga_progress_viewed` — once, on the first non-null snapshot (never on a bare mount).
 * - `saga_progress_completed` / `saga_progress_failed` — once, when a run this view watched while
 *   it was still in flight reaches that outcome. A view that opens on an already-finished run saw
 *   no transition and reports none, so a returning visitor is not counted as a second completion.
 * - `saga_progress_unexpected_wait` — once per `collect_input` step seen `waiting`: an approach that
 *   needs input mid-run is a defect upstream, and this is how it is found.
 *
 * `estimateErrorMs` on completion is the actual duration minus the last estimate's projected
 * total (elapsed + remaining at the time) — the signal that tunes the estimate (D6). It is `null`
 * when no estimate was ever offered.
 */
export function useSagaProgressAnalytics(progress: EmbeddedSagaProgress | null, surface: SagaProgressSurface): void {
  const logAnalyticsEvent = useAnalytics();
  const viewedRef = useRef(false);
  const sawInFlightRunRef = useRef<string | null>(null);
  const reportedOutcomeRef = useRef<string | null>(null);
  const reportedWaitsRef = useRef(new Set<string>());
  const lastProjectedTotalMsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!progress) return;

    const run = isRun(progress) ? progress : null;
    const common = {
      kind: run?.kind ?? null,
      approachSystemName: progress.subject?.approachSystemName ?? null,
      surface,
    };

    if (!viewedRef.current) {
      viewedRef.current = true;
      logAnalyticsEvent('saga_progress_viewed', undefined, common);
    }

    if (!run) return;

    const isFinished = run.status === 'completed' || run.status === 'failed' || run.status === 'compensated';

    if (!isFinished) {
      sawInFlightRunRef.current = run.runId;
      if (run.estimate.remainingMs != null) {
        lastProjectedTotalMsRef.current = run.elapsedMs + run.estimate.remainingMs;
      }
      for (const step of run.steps) {
        if (step.type === 'collect_input' && step.status === 'waiting' && !reportedWaitsRef.current.has(step.id)) {
          reportedWaitsRef.current.add(step.id);
          logAnalyticsEvent('saga_progress_unexpected_wait', undefined, { runId: run.runId, stepId: step.id });
        }
      }
      return;
    }

    if (sawInFlightRunRef.current !== run.runId || reportedOutcomeRef.current === run.runId) return;
    reportedOutcomeRef.current = run.runId;

    if (run.status === 'completed') {
      const projected = lastProjectedTotalMsRef.current;
      logAnalyticsEvent('saga_progress_completed', undefined, {
        ...common,
        elapsedMs: run.elapsedMs,
        stepCount: run.counts.total,
        estimateConfidence: run.estimate.confidence,
        estimateErrorMs: projected == null ? null : run.elapsedMs - projected,
      });
      return;
    }

    const failedStep = run.outcome?.kind === 'failed' ? run.outcome.failedStep : null;
    const remainingSideEffects =
      run.outcome && run.outcome.kind !== 'completed' ? run.outcome.remainingSideEffects : false;
    logAnalyticsEvent('saga_progress_failed', undefined, {
      ...common,
      failedStepType: failedStep?.type ?? null,
      elapsedMs: run.elapsedMs,
      remainingSideEffects,
    });
  }, [progress, surface, logAnalyticsEvent]);
}

export default useSagaProgressAnalytics;
